/**
 * The MCP server shares the pod with the customer API, served from the `mcp.`
 * subdomain. Derive its URL rather than adding another field to pod config.
 */
export const getMcpApiUrl = (customerApiUrl: string): string =>
  customerApiUrl.replace('//api.', '//mcp.').replace('/graphql', '/mcp');

/**
 * The workspace-scoped MCP URL users paste into their MCP host. The
 * workspace travels through OAuth discovery as the RFC 8707 resource.
 */
export const getWorkspaceMcpUrl = (
  customerApiUrl: string,
  workspaceId: string
): string =>
  `${getMcpApiUrl(customerApiUrl)}/w/${encodeURIComponent(workspaceId)}`;

/**
 * Server identifier users register the MCP under in their host config. One MCP
 * server is needed per workspace, so the workspace name is slugged into the
 * name to keep each host entry unique and config-key safe (no spaces/casing).
 */
export const getWorkspaceMcpServerName = (workspaceName: string): string => {
  const slug = workspaceName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `fragment-mcp-${slug}` : 'fragment-mcp';
};

/**
 * RFC 9728 protected-resource metadata URL for the workspace MCP resource;
 * its `authorization_servers` names the MCP OAuth issuer.
 */
export const getMcpProtectedResourceMetadataUrl = (
  customerApiUrl: string,
  workspaceId: string
): string => {
  const mcpUrl = new URL(getWorkspaceMcpUrl(customerApiUrl, workspaceId));
  return `${mcpUrl.origin}/.well-known/oauth-protected-resource${mcpUrl.pathname}`;
};
